/** @format */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableHead,
    TableRow,
    MenuItem,
    Select,
    CircularProgress,
    IconButton,
    Tooltip,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    FormControl,
    InputLabel,
    OutlinedInput,
    TextField,
    Stack,
    Divider,
} from "@mui/material";
import {
    Refresh,
    OpenInNew,
    MarkEmailReadOutlined,
    AutorenewOutlined,
    CheckCircleOutlineOutlined,
    InboxOutlined,
} from "@mui/icons-material";

import { AppDispatch } from "../../redux/store/store";
import {
    fetchReplies,
    fetchRepliesSummary,
    reprocessReply,
    resolveReply,
} from "../../redux/actions/collectAction";
import {
    selectActiveBusinessId,
    selectReplies,
    selectRepliesCursor,
    selectIsLoadingReplies,
    selectRepliesError,
    selectRepliesSummary,
    selectIsLoadingRepliesSummary,
    selectIsProcessingReply,
} from "../../redux/selectors/collectSelector";
import {
    ICustomerReply,
    ReplyIntent,
    ReplyStatus,
    REPLY_INTENT_LABELS,
    REPLY_STATUS_LABELS,
} from "../../utility/types/collectTypes";
import { useToast } from "../../providers/ToastProvider";
import {
    CollectPageWrapper,
    CollectSectionCard,
    CollectSectionHeader,
    CollectSectionTitle,
    CollectSectionSubtitle,
    CollectTableContainer,
    CollectTableHeadCell,
    CollectTableBodyCell,
    CollectEmptyState,
    CollectEmptyTitle,
    CollectEmptyHint,
    CollectPrimaryButton,
    CollectGhostButton,
    CollectKpiCard,
    CollectKpiLabel,
    CollectKpiValue,
    CollectKpiHint,
    FlexBetween,
} from "./styles";
import ChannelBadge from "./ChannelBadge";

// ─────────────────────────────────────────────────────────────────────────
// CollectReplies (Stage 2D) — inbox of inbound customer replies.
// The classifier auto-routes most messages, but operators triage anything
// marked NEEDS_REVIEW (questions, disputes, ambiguous wording, complaints).
// ─────────────────────────────────────────────────────────────────────────

const INTENT_COLORS: Record<ReplyIntent, string> = {
    payment_commitment: "#16a34a",
    already_paid: "#0ea5e9",
    dispute: "#dc2626",
    question: "#f59e0b",
    complaint: "#dc2626",
    unsubscribe: "#6b7280",
    auto_response: "#94a3b8",
    other: "#64748b",
};

const STATUS_COLORS: Record<ReplyStatus, string> = {
    pending: "#94a3b8",
    processed: "#16a34a",
    needs_review: "#f59e0b",
    failed: "#dc2626",
    resolved: "#0ea5e9",
};

function formatDate(value?: string): string {
    if (!value) return "—";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "—";
    try {
        return d.toLocaleString();
    } catch {
        return value;
    }
}

function formatDateOnly(value?: string): string {
    if (!value) return "—";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, {
        weekday: "long",
        month: "short",
        day: "numeric",
        year: "numeric",
    });
}

function formatCurrency(amount?: number, currency = "USD"): string {
    if (typeof amount !== "number" || !isFinite(amount)) return "—";
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
        }).format(amount);
    } catch {
        return `$${amount.toFixed(2)}`;
    }
}

/**
 * Turn a raw classifier reason token into a sentence the business owner
 * can actually read. We surface AI reasoning prominently and quietly drop
 * the internal heuristic-debug breadcrumbs.
 */
function friendlyReason(raw: string): string | null {
    if (!raw) return null;
    if (raw.startsWith("llm:")) {
        return raw.slice(4).trim();
    }
    // Internal debug breadcrumbs — hide from operator view.
    if (raw.startsWith("heuristic_was:")) return null;
    if (raw === "llm") return null;
    // Pretty-print short tokens like "keyword:paid" → "Keyword match: paid".
    if (raw.includes(":")) {
        const [k, v] = raw.split(":", 2);
        return `${k.replace(/_/g, " ")}: ${v}`;
    }
    return raw.replace(/_/g, " ");
}

const ACTION_LABELS: Record<string, string> = {
    stamped_lastCustomerReplyAt: "Logged customer reply on invoice",
    created_promise: "Created promise-to-pay",
    promise_already_active: "Promise-to-pay already active — no change",
    transitioned_to_disputed: "Moved invoice to Disputed",
    already_disputed_or_not_allowed:
        "Invoice already disputed or transition not allowed",
    transitioned_to_manual_review: "Queued for manual review",
    customer_opted_out: "Customer opted out of further emails",
    customer_already_opted_out: "Customer was already opted out",
    auto_response_no_action: "Auto-response detected — no action taken",
};

function friendlyAction(entry: any): string {
    if (typeof entry === "string") {
        // Stage 2G auto-reply actions are namespaced "auto_replied:<kind>" /
        // "auto_reply_skipped:<reason>" — render them conversationally.
        if (entry.startsWith("auto_replied:")) {
            const kind = entry.slice("auto_replied:".length).replace(/_/g, " ");
            return `Auto-replied — ${kind}`;
        }
        if (entry.startsWith("auto_reply_skipped:")) {
            const reason = entry
                .slice("auto_reply_skipped:".length)
                .replace(/_/g, " ");
            return `Auto-reply skipped — ${reason}`;
        }
        return ACTION_LABELS[entry] || entry.replace(/_/g, " ");
    }
    if (entry && typeof entry === "object" && typeof entry.type === "string") {
        const label = ACTION_LABELS[entry.type] || entry.type.replace(/_/g, " ");
        const when = entry.at ? formatDate(entry.at) : null;
        return when && when !== "—" ? `${label} — ${when}` : label;
    }
    return String(entry);
}

function truncate(value: string | undefined, n: number): string {
    if (!value) return "";
    return value.length > n ? `${value.slice(0, n)}…` : value;
}

// ─────────────────────────────────────────────────────────────────────────

const CollectReplies: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const toast = useToast();

    const businessId = useSelector(selectActiveBusinessId);
    const replies = useSelector(selectReplies);
    const cursor = useSelector(selectRepliesCursor);
    const isLoading = useSelector(selectIsLoadingReplies);
    const error = useSelector(selectRepliesError);
    const summary = useSelector(selectRepliesSummary);
    const isLoadingSummary = useSelector(selectIsLoadingRepliesSummary);
    const isProcessing = useSelector(selectIsProcessingReply);

    const [statusFilter, setStatusFilter] = useState<ReplyStatus | "">("");
    const [intentFilter, setIntentFilter] = useState<ReplyIntent | "">("");

    const [viewing, setViewing] = useState<ICustomerReply | null>(null);
    const [resolving, setResolving] = useState<ICustomerReply | null>(null);
    const [resolveNote, setResolveNote] = useState("");

    const buildQuery = (append = false) => ({
        append,
        status: statusFilter || undefined,
        intent: intentFilter || undefined,
        cursor: append ? cursor || undefined : undefined,
        limit: 25,
    });

    useEffect(() => {
        if (!businessId) return;
        dispatch(fetchReplies(buildQuery(false)));
        dispatch(fetchRepliesSummary({}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId, statusFilter, intentFilter, dispatch]);

    const handleRefresh = () => {
        if (!businessId) return;
        dispatch(fetchReplies(buildQuery(false)));
        dispatch(fetchRepliesSummary({}));
    };

    const handleLoadMore = () => {
        if (!businessId || !cursor) return;
        dispatch(fetchReplies(buildQuery(true)));
    };

    const handleReprocess = async (replyId: string) => {
        const action: any = await dispatch(reprocessReply({ replyId }));
        if (reprocessReply.fulfilled.match(action)) {
            toast.success("Reply re-classified.");
            dispatch(fetchRepliesSummary({}));
        } else {
            toast.error(
                (action.payload as string) || "Failed to reprocess reply.",
            );
        }
    };

    const openResolveDialog = (reply: ICustomerReply) => {
        setResolving(reply);
        setResolveNote("");
    };

    const handleConfirmResolve = async () => {
        if (!resolving) return;
        const action: any = await dispatch(
            resolveReply({
                replyId: resolving._id,
                note: resolveNote.trim() || undefined,
            }),
        );
        if (resolveReply.fulfilled.match(action)) {
            toast.success("Reply resolved.");
            setResolving(null);
            setResolveNote("");
            dispatch(fetchRepliesSummary({}));
        } else {
            toast.error(
                (action.payload as string) || "Failed to resolve reply.",
            );
        }
    };

    const totalReplies = summary?.total ?? 0;
    const needsReview = summary?.needsReview ?? 0;
    const processed =
        (summary?.byStatus?.processed ?? 0) +
        (summary?.byStatus?.resolved ?? 0);
    const intentChips = useMemo(() => {
        const entries = Object.entries(summary?.byIntent || {}) as Array<
            [ReplyIntent, number]
        >;
        return entries.sort((a, b) => b[1] - a[1]).slice(0, 5);
    }, [summary]);

    return (
        <CollectPageWrapper>
            <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: "auto" }}>
                {/* ── Summary header ───────────────────────────── */}
                <CollectSectionCard sx={{ mb: 3 }}>
                    <CollectSectionHeader>
                        <Box>
                            <CollectSectionTitle>Reply inbox</CollectSectionTitle>
                            <CollectSectionSubtitle>
                                Inbound customer replies, classified automatically.
                                Triage anything flagged for review.
                            </CollectSectionSubtitle>
                        </Box>
                        <Tooltip title="Refresh">
                            <IconButton onClick={handleRefresh} disabled={isLoading}>
                                <Refresh />
                            </IconButton>
                        </Tooltip>
                    </CollectSectionHeader>

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={4}>
                            <CollectKpiCard>
                                <CollectKpiLabel>Total replies</CollectKpiLabel>
                                <CollectKpiValue>
                                    {isLoadingSummary ? "…" : totalReplies}
                                </CollectKpiValue>
                                <CollectKpiHint>All time</CollectKpiHint>
                            </CollectKpiCard>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <CollectKpiCard>
                                <CollectKpiLabel>Needs review</CollectKpiLabel>
                                <CollectKpiValue
                                    style={{ color: needsReview > 0 ? "#d97706" : undefined }}
                                >
                                    {isLoadingSummary ? "…" : needsReview}
                                </CollectKpiValue>
                                <CollectKpiHint>Awaiting human triage</CollectKpiHint>
                            </CollectKpiCard>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <CollectKpiCard>
                                <CollectKpiLabel>Auto-processed</CollectKpiLabel>
                                <CollectKpiValue>
                                    {isLoadingSummary ? "…" : processed}
                                </CollectKpiValue>
                                <CollectKpiHint>Resolved without human action</CollectKpiHint>
                            </CollectKpiCard>
                        </Grid>
                    </Grid>

                    {intentChips.length > 0 && (
                        <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {intentChips.map(([intent, count]) => (
                                <Chip
                                    key={intent}
                                    size="small"
                                    label={`${REPLY_INTENT_LABELS[intent]}: ${count}`}
                                    sx={{
                                        backgroundColor: `${INTENT_COLORS[intent]}1A`,
                                        color: INTENT_COLORS[intent],
                                        fontWeight: 600,
                                    }}
                                />
                            ))}
                        </Box>
                    )}
                </CollectSectionCard>

                {/* ── Filters ──────────────────────────────────── */}
                <CollectSectionCard sx={{ mb: 3 }}>
                    <FlexBetween sx={{ flexWrap: "wrap", gap: 2 }}>
                        <FormControl sx={{ minWidth: 180 }} size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={statusFilter}
                                onChange={(e) =>
                                    setStatusFilter(e.target.value as ReplyStatus | "")
                                }
                                input={<OutlinedInput label="Status" />}
                            >
                                <MenuItem value="">All</MenuItem>
                                {(Object.keys(REPLY_STATUS_LABELS) as ReplyStatus[]).map(
                                    (s) => (
                                        <MenuItem key={s} value={s}>
                                            {REPLY_STATUS_LABELS[s]}
                                        </MenuItem>
                                    ),
                                )}
                            </Select>
                        </FormControl>

                        <FormControl sx={{ minWidth: 220 }} size="small">
                            <InputLabel>Intent</InputLabel>
                            <Select
                                value={intentFilter}
                                onChange={(e) =>
                                    setIntentFilter(e.target.value as ReplyIntent | "")
                                }
                                input={<OutlinedInput label="Intent" />}
                            >
                                <MenuItem value="">All</MenuItem>
                                {(Object.keys(REPLY_INTENT_LABELS) as ReplyIntent[]).map(
                                    (i) => (
                                        <MenuItem key={i} value={i}>
                                            {REPLY_INTENT_LABELS[i]}
                                        </MenuItem>
                                    ),
                                )}
                            </Select>
                        </FormControl>

                        <Box sx={{ flexGrow: 1 }} />

                        <CollectGhostButton
                            startIcon={<MarkEmailReadOutlined />}
                            onClick={() => setStatusFilter("needs_review")}
                        >
                            Show needs review
                        </CollectGhostButton>
                    </FlexBetween>
                </CollectSectionCard>

                {/* ── Table ────────────────────────────────────── */}
                <CollectSectionCard>
                    {error && (
                        <Typography color="error" sx={{ mb: 2 }}>
                            {error}
                        </Typography>
                    )}

                    <CollectTableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <CollectTableHeadCell>Received</CollectTableHeadCell>
                                    <CollectTableHeadCell>From</CollectTableHeadCell>
                                    <CollectTableHeadCell>Subject</CollectTableHeadCell>
                                    <CollectTableHeadCell>Intent</CollectTableHeadCell>
                                    <CollectTableHeadCell>Status</CollectTableHeadCell>
                                    <CollectTableHeadCell>Invoice</CollectTableHeadCell>
                                    <CollectTableHeadCell align="right">
                                        Actions
                                    </CollectTableHeadCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {replies.map((r) => {
                                    const intent = r.classification?.intent;
                                    const conf = r.classification?.confidence;
                                    return (
                                        <TableRow key={r._id} hover>
                                            <CollectTableBodyCell>
                                                {formatDate(r.receivedAt)}
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.75,
                                                        flexWrap: "wrap",
                                                    }}
                                                >
                                                    <ChannelBadge
                                                        channel={r.channel}
                                                        size="xs"
                                                    />
                                                    {truncate(r.fromAddress, 40)}
                                                </Box>
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell>
                                                {truncate(r.subject || "(no subject)", 60)}
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell>
                                                {intent ? (
                                                    <Chip
                                                        size="small"
                                                        label={`${REPLY_INTENT_LABELS[intent]}${typeof conf === "number"
                                                            ? ` · ${Math.round(conf * 100)}%`
                                                            : ""
                                                            }`}
                                                        sx={{
                                                            backgroundColor: `${INTENT_COLORS[intent]}1A`,
                                                            color: INTENT_COLORS[intent],
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">
                                                        —
                                                    </Typography>
                                                )}
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell>
                                                <Chip
                                                    size="small"
                                                    label={REPLY_STATUS_LABELS[r.status]}
                                                    sx={{
                                                        backgroundColor: `${STATUS_COLORS[r.status]}1A`,
                                                        color: STATUS_COLORS[r.status],
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell>
                                                {r.invoiceId ? (
                                                    <Tooltip title="Open invoice">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                navigate(
                                                                    `/collect/invoices/${r.invoiceId}`,
                                                                )
                                                            }
                                                        >
                                                            <OpenInNew fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">
                                                        —
                                                    </Typography>
                                                )}
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell align="right">
                                                <Tooltip title="View">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setViewing(r)}
                                                    >
                                                        <InboxOutlined fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Re-classify">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            disabled={isProcessing}
                                                            onClick={() => handleReprocess(r._id)}
                                                        >
                                                            <AutorenewOutlined fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                {r.status === "needs_review" && (
                                                    <Tooltip title="Mark resolved">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={isProcessing}
                                                                onClick={() => openResolveDialog(r)}
                                                            >
                                                                <CheckCircleOutlineOutlined fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </CollectTableBodyCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </CollectTableContainer>

                    {!isLoading && replies.length === 0 && (
                        <CollectEmptyState>
                            <MarkEmailReadOutlined
                                sx={{ fontSize: 40, color: "#94a3b8" }}
                            />
                            <CollectEmptyTitle>
                                {businessId ? "Inbox is empty" : "Select a business"}
                            </CollectEmptyTitle>
                            <CollectEmptyHint>
                                {businessId
                                    ? "No customer replies match the current filters."
                                    : "Pick an active business from the top nav to load the inbox."}
                            </CollectEmptyHint>
                        </CollectEmptyState>
                    )}

                    {isLoading && (
                        <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}

                    {cursor && !isLoading && (
                        <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
                            <CollectPrimaryButton onClick={handleLoadMore}>
                                Load more
                            </CollectPrimaryButton>
                        </Box>
                    )}
                </CollectSectionCard>
            </Box>

            {/* ── View dialog ──────────────────────────────────── */}
            <Dialog
                open={!!viewing}
                onClose={() => setViewing(null)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>{viewing?.subject || "(no subject)"}</DialogTitle>
                <DialogContent dividers>
                    {viewing && (
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    From
                                </Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <ChannelBadge channel={viewing.channel} />
                                    <Typography variant="body2">
                                        {viewing.fromAddress} → {viewing.toAddress}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Received
                                </Typography>
                                <Typography variant="body2">
                                    {formatDate(viewing.receivedAt)}
                                </Typography>
                            </Box>
                            {viewing.classification && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Classification
                                    </Typography>
                                    <Typography variant="body2">
                                        {REPLY_INTENT_LABELS[viewing.classification.intent]} ·{" "}
                                        {Math.round(viewing.classification.confidence * 100)}%
                                        confidence
                                    </Typography>
                                    {viewing.classification.extracted && (
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            flexWrap="wrap"
                                            sx={{ mt: 1, rowGap: 1 }}
                                        >
                                            {viewing.classification.extracted.promisedDate && (
                                                <Chip
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                    label={`Promised to pay by ${formatDateOnly(
                                                        viewing.classification.extracted
                                                            .promisedDate,
                                                    )}`}
                                                />
                                            )}
                                            {typeof viewing.classification.extracted
                                                .promisedAmount === "number" && (
                                                    <Chip
                                                        size="small"
                                                        color="success"
                                                        variant="outlined"
                                                        label={`Promised amount: ${formatCurrency(
                                                            viewing.classification.extracted
                                                                .promisedAmount,
                                                        )}`}
                                                    />
                                                )}
                                            {viewing.classification.extracted.invoiceNumber && (
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={`Invoice #${viewing.classification.extracted.invoiceNumber}`}
                                                />
                                            )}
                                        </Stack>
                                    )}
                                    {(() => {
                                        const friendly = (
                                            viewing.classification.reasons || []
                                        )
                                            .map(friendlyReason)
                                            .filter(
                                                (s): s is string =>
                                                    !!s && s.length > 0,
                                            );
                                        if (friendly.length === 0) return null;
                                        return (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    display: "block",
                                                    mt: 1,
                                                    fontStyle: "italic",
                                                }}
                                            >
                                                AI reasoning: {friendly.join(" · ")}
                                            </Typography>
                                        );
                                    })()}
                                </Box>
                            )}
                            <Divider />
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Body
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        fontFamily:
                                            "ui-monospace, SFMono-Regular, Menlo, monospace",
                                        fontSize: 13,
                                        backgroundColor: "#f8fafc",
                                        border: "1px solid #e2e8f0",
                                        borderRadius: 1,
                                        p: 1.5,
                                        maxHeight: 320,
                                        overflow: "auto",
                                    }}
                                >
                                    {viewing.bodyText || "(empty)"}
                                </Typography>
                            </Box>
                            {viewing.processedActions &&
                                viewing.processedActions.length > 0 && (
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Actions taken
                                        </Typography>
                                        {viewing.processedActions.map((a, i) => (
                                            <Typography
                                                key={i}
                                                variant="body2"
                                                sx={{ fontSize: 13 }}
                                            >
                                                · {friendlyAction(a)}
                                            </Typography>
                                        ))}
                                    </Box>
                                )}
                            {viewing.reviewNote && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Reviewer note
                                    </Typography>
                                    <Typography variant="body2">
                                        {viewing.reviewNote}
                                    </Typography>
                                </Box>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <CollectGhostButton onClick={() => setViewing(null)}>
                        Close
                    </CollectGhostButton>
                </DialogActions>
            </Dialog>

            {/* ── Resolve dialog ───────────────────────────────── */}
            <Dialog
                open={!!resolving}
                onClose={() => setResolving(null)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Resolve reply</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Mark this reply as resolved. Add an optional note for the
                        audit log.
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Resolution note (optional)"
                        value={resolveNote}
                        onChange={(e) => setResolveNote(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <CollectGhostButton onClick={() => setResolving(null)}>
                        Cancel
                    </CollectGhostButton>
                    <CollectPrimaryButton
                        onClick={handleConfirmResolve}
                        disabled={isProcessing}
                    >
                        Resolve
                    </CollectPrimaryButton>
                </DialogActions>
            </Dialog>
        </CollectPageWrapper>
    );
};

export default CollectReplies;
